import { PlusOutlined } from '@ant-design/icons';
import { Button, Empty, Space, Typography } from 'antd';
import axios from 'axios';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import box from '../../assets/img/fi_box_blue.svg';
import PPTableLink from '../../components/link/PPTableLink';
import PPResizableTable from '../../components/table/PPResizableTable';
import { useTableFilter } from '../../utils/Filter';
import { getIndexColumn } from '../../utils/getIndexColumn';
import InventoryStatusBadge from './components/InventoryStatusBadge';
import './Order.less';

const { Text } = Typography;

const val = (value) => (value === null || value === undefined || value === '' ? '-' : value);

/** Огноог уншигдахуйц болгоно */
const formatDate = (value) => (value ? moment(value).format('YYYY-MM-DD') : '-');

// meta-гээс ирсэн enum-ыг enumDictionary-аар нэрлэж шүүлтүүрийн сонголт болгох
// (enumDictionary дээр нэр нь байхгүй утгуудыг харуулахгүй)
const getEnumFilters = (field) =>
  (field?.enum ?? [])
    .filter((value) => field?.enumDictionary?.[value])
    .map((value) => ({
      text: field.enumDictionary[value],
      value,
    }));

const getColumns = (getColumnSearchProps, getColumnFilterProps, pagination, meta) => [
  getIndexColumn(pagination.current, pagination.pageSize),
  {
    title: 'Захиалгын дугаар',
    dataIndex: 'code',
    key: 'code',
    width: 150,
    ...getColumnSearchProps('code', 'Захиалгын дугаар'),
    render: (text, record) => (
      <PPTableLink to={{ pathname: `/merchant/inventory/controls/${record._id}` }}>
        {val(text)}
      </PPTableLink>
    ),
  },
  {
    /**
     * `inventoryDate` нь тооллого ХИЙХ огноо — хүсэлт үүссэн өдөр биш.
     * Багана «Үүссэн огноо» гэж нэрлэгдсэн байсан тул хэтийн огноо сонгосон
     * тооллого жагсаалтад ирээдүйд үүссэн шиг харагддаг байв. Сервер тал
     * жагсаалтад `createdAt`-ыг буцаадаггүй тул баганыг зөв нэрээр нэрлэв.
     */
    title: 'Тооллого хийх огноо',
    dataIndex: 'inventoryDate',
    key: 'inventoryDate',
    width: 200,
    render: formatDate,
  },
  {
    title: 'Дууссан огноо',
    dataIndex: 'operatedDate',
    key: 'operatedDate',
    width: 200,
    render: formatDate,
  },
  {
    title: 'Төлөв',
    dataIndex: 'orderStatus',
    key: 'orderStatus',
    width: 200,
    filters: getEnumFilters(meta?.orderStatus),
    ...getColumnFilterProps('orderStatus', { multiple: false }),
    render: (orderStatus) => (
      <InventoryStatusBadge
        status={orderStatus}
        label={meta?.orderStatus?.enumDictionary?.[orderStatus]}
      />
    ),
  },
];

const InventoryListItem = () => {
  const [docs, setDocs] = useState([]);
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [contractType, setContractType] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  // Тооллого дата авах хүсэлт. `tab` нь List.js-ийн табын URL параметр тул
  // шүүлтүүр болгож API руу явуулахгүй.
  const fetchData = async ({ page = 1, pageSize = 10, tab, ...filters } = {}) => {
    setLoading(true);
    try {
      const { data } = await axios.post('/api/v1/request/list-invcount', {
        page,
        pageSize,
        ...filters,
      });
      setDocs(data?.result?.docs ?? []);
      setMeta(data?.result?.meta ?? {});
      setPagination({
        current: data?.result?.page ?? page,
        pageSize: data?.result?.limit ?? pageSize,
        total: data?.result?.totalDocs ?? 0,
      });
    } catch (error) {
      console.error('fetchInventoryList: ', error);
    } finally {
      setLoading(false);
    }
  };

  // Анхны ачаалалт болон шүүлт солигдох үед hook өөрөө fetchData-г дуудна
  const { getColumnSearchProps, getColumnFilterProps, handleTableChange, withHighlight, filters } =
    useTableFilter({ fetchData });

  useEffect(() => {
    axios
      .get('/api/v1/request/delivery-reference')
      .then(({ data }) => {
        if (data.statusCode === 200) {
          const contractTypeData = data?.result?.contract?.data?.contractType;
          setContractType(Array.isArray(contractTypeData) ? contractTypeData : []);
        }
      })
      .catch((error) => {
        console.error('delivery-reference: ', error);
      });
  }, []);

  const columns = withHighlight(
    getColumns(getColumnSearchProps, getColumnFilterProps, pagination, meta)
  );

  /** Тооллого үүсгэх эрх нь агуулахын гэрээтэй (WO, W) харилцагчид л байна */
  const canCreate =
    Array.isArray(contractType) && contractType.some((type) => type === 'WO' || type === 'W');

  const createButton = (
    <Link to="/merchant/inventory/control/add">
      <Button type="primary" icon={<PlusOutlined />}>
        Шинэ
      </Button>
    </Link>
  );

  // Шүүлт хийсэн үед хоосон гарвал хүснэгтээ (шүүлтүүрийн хамт) харуулна,
  // эс тэгвээс шүүлтээ цэвэрлэх боломжгүй болно. `tab` нь шүүлт биш.
  const hasFilters = Object.keys(filters).some((key) => key !== 'tab');

  if (!loading && docs.length === 0 && !hasFilters) {
    return (
      <div className="pp_table_wrapper">
        <Empty
          image={box}
          imageStyle={{ height: 60 }}
          style={{ padding: '32px 0' }}
          description={
            <Space direction="vertical" size={4}>
              <Text strong>Тооллого бүртгэгдээгүй байна</Text>
              {canCreate && (
                <Text type="secondary">Та доорх товч дээр дарж тооллого үүсгэнэ үү.</Text>
              )}
            </Space>
          }
        >
          {canCreate && createButton}
        </Empty>
      </div>
    );
  }

  return (
    <div className="pp_table_wrapper">
      {canCreate && <div className="inventory_tab_header">{createButton}</div>}
      <PPResizableTable
        dataSource={docs}
        columns={columns}
        size="large"
        rowKey="_id"
        loading={loading}
        onChange={handleTableChange}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showTotal: (total) => `Нийт ${total} илэрц`,
        }}
        scroll={{ x: 'max-content' }}
      />
    </div>
  );
};

export default InventoryListItem;
